import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ShieldCheck, TrendingUp, Bot, Info } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

// === Nova Home — Swiss Minimalist with Pastel Accents ============
// Pastel palette (kept inline as hex per design guidelines):
//   #EBD8C8 — warm peach
//   #D4E0DA — soft sage
//   #D9CFD9 — quiet lavender
// Foreground / background:
//   #0A0A0A on #FAFAFA
// Typography utilities live in /src/index.css:
//   .font-display (Playfair Display) · .font-body (Manrope) · .font-label (Outfit)
// =====================================================================

const FRAMEWORKS = [
  { label: 'SOC 2', id: 'soc-2' },
  { label: 'HIPAA', id: 'hipaa' },
  { label: 'GDPR', id: 'gdpr' },
  { label: 'PCI DSS', id: 'pci-dss' },
  { label: 'ISO 27001', id: 'iso-27001' },
];

// Hero product-style visual — an on-brand "Audit Readiness" dashboard card.
const HeroVisual = () => {
  const R = 42;
  const C = 2 * Math.PI * R;
  const score = 92;
  const rows = [
    { name: 'SOC 2', pct: 96, tintClass: 'bg-[#D4E0DA] dark:bg-[#101F18]' },
    { name: 'ISO 27001', pct: 88, tintClass: 'bg-[#EBD8C8] dark:bg-[#2E1B10]' },
    { name: 'GDPR', pct: 81, tintClass: 'bg-[#D9CFD9] dark:bg-[#1E1724]' },
  ];
  return (
    <div className="relative mx-auto w-full max-w-md" data-testid="hero-visual">
      {/* Main card */}
      <div className="rounded-3xl bg-white dark:bg-[#111111] border border-black/10 dark:border-white/10 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.22)] p-7">
        <div className="flex items-center justify-between mb-7">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#D4E0DA] dark:bg-[#101F18] flex items-center justify-center">
              <ShieldCheck className="w-[18px] h-[18px] text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
            </div>
            <span className="font-display font-semibold text-[15px] text-[#0A0A0A] dark:text-[#F5F5F5]">Audit Readiness</span>
          </div>
          <span className="font-label text-[10px] uppercase tracking-[0.16em] text-[#0A0A0A] dark:text-[#F5F5F5] bg-[#D4E0DA]/70 dark:bg-[#101F18]/70 px-3 py-1 rounded-full">
            On track
          </span>
        </div>

        {/* Score ring */}
        <div className="flex items-center gap-5 mb-7">
          <div className="relative w-24 h-24 shrink-0">
            <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
              <circle cx="50" cy="50" r={R} fill="none" className="stroke-black/[0.08] dark:stroke-white/10" strokeWidth="8" />
              <circle
                cx="50" cy="50" r={R} fill="none" className="stroke-[#0F62FE] dark:stroke-[#2E79FF]" strokeWidth="8"
                strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - score / 100)}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-display font-semibold text-2xl leading-none text-[#0A0A0A] dark:text-[#F5F5F5]">{score}%</span>
            </div>
          </div>
          <div>
            <p className="font-display text-sm text-[#0A0A0A] dark:text-[#F5F5F5] leading-snug">Controls ready for review</p>
            <p className="text-xs text-black/50 dark:text-white/55 mt-1">128 of 139 controls mapped</p>
          </div>
        </div>

        {/* Framework progress rows */}
        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.name} className="flex items-center gap-3">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${r.tintClass}`}>
                <Check className="w-3.5 h-3.5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[13px] text-[#0A0A0A] dark:text-[#F5F5F5]">{r.name}</span>
                  <span className="font-label text-[11px] text-black/50 dark:text-white/55">{r.pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-black/[0.06] dark:bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-[#0A0A0A]/80 dark:bg-white/80" style={{ width: `${r.pct}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating accent card */}
      <div className="absolute -bottom-9 -right-4 bg-white dark:bg-[#111111] border border-black/10 dark:border-white/10 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.2)] rounded-2xl px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#EBD8C8] dark:bg-[#2E1B10] flex items-center justify-center">
          <TrendingUp className="w-[18px] h-[18px] text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
        </div>
        <div>
          <p className="font-display font-semibold text-sm text-[#0A0A0A] dark:text-[#F5F5F5] leading-none">2,480</p>
          <p className="text-[11px] text-black/50 dark:text-white/55 mt-1">Evidence items auto-collected</p>
        </div>
      </div>
    </div>
  );
};


const Home = () => {
  const navigate = useNavigate();

  const readinessBullets = [
    'Collect real evidence from your systems',
    'Map controls across SOC 2, HIPAA, GDPR, PCI DSS and ISO 27001.',
    'Identify gaps and track remediation',
    'Build complete audit-ready documentation',
  ];
  const certificationBullets = [
    'Trusted audit partners',
    'Auditor selection assistance',
    'Evidence sharing and validation support',
    'Guidance throughout the audit process',
  ];
  const processSteps = [
    { num: '01', title: 'Assess Your Readiness' },
    { num: '02', title: 'Connect Your Systems' },
    { num: '03', title: 'Prepare for Audit' },
    { num: '04', title: 'Independent Auditor Review' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#D4E0DA]/55 dark:bg-[#101F18]/55 px-6 pt-[9.75rem] md:pt-[11.85rem] pb-[8.75rem] md:pb-[11.85rem]">
        {/* Subtle light sheen over the pastel hero — premium depth, no dark blobs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(60% 55% at 50% -5%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 70%)',
          }}
        />
        <div className="max-w-4xl mx-auto w-full text-center">
          <h1
            data-testid="hero-headline"
            className="font-display tracking-tight text-[#0A0A0A] dark:text-[#F5F5F5]"
          >
            <span className="block font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
              SOC&nbsp;2 Compliance Platform
            </span>
            <span
              data-testid="hero-subheading"
              className="block font-normal text-black/55 dark:text-white/55 text-base sm:text-lg leading-snug mt-4 max-w-xl mx-auto"
            >
              Also supports HIPAA, GDPR, PCI&nbsp;DSS and ISO&nbsp;27001
            </span>
          </h1>

          {/* CTAs — side by side on desktop, stacked on mobile */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="hero-get-started"
              className="font-label w-full sm:w-auto bg-[#0F62FE] dark:bg-[#2E79FF] text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] shadow-sm hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] hover:shadow-md transition-all duration-300"
            >
              Start Free Trial
            </button>
            <button
              type="button"
              onClick={() => navigate('/contact')}
              data-testid="hero-book-demo"
              className="font-label w-full sm:w-auto border border-[#0A0A0A]/20 dark:border-white/25 text-[#0A0A0A] dark:text-[#F5F5F5] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-white/70 dark:hover:bg-[#111111]/70 transition-all duration-300"
            >
              Book a Demo
            </button>
          </div>
          <p className="font-label text-[11px] uppercase tracking-[0.2em] text-black/45 dark:text-white/50 mt-6">
            30-day free trial · No credit card required · Cancel anytime
          </p>
        </div>
      </section>

      {/* ===================== FRAMEWORK BADGES (separator) ===================== */}
      <section className="border-y border-black/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-[#050505] px-6 py-7 md:py-9">
        <div
          data-testid="hero-framework-strip"
          className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-2.5 md:gap-3"
        >
          {FRAMEWORKS.map((fw) => (
            <span
              key={fw.id}
              data-testid={`hero-framework-${fw.id}`}
              className="font-label text-[11px] md:text-xs tracking-[0.14em] text-black/55 dark:text-white/55 bg-black/[0.025] dark:bg-white/[0.04] border border-black/10 dark:border-white/10 px-4 py-2 rounded-full hover:border-black/25 dark:hover:border-white/25 hover:text-black/75 dark:hover:text-white/75 transition-colors duration-300"
            >
              {fw.label}
            </span>
          ))}
        </div>
      </section>

      {/* ===================== READINESS + AUDIT SUPPORT ===================== */}
      <section className="px-6 bg-[#FAFAFA] dark:bg-[#050505]">
        <div className="max-w-5xl mx-auto border-b border-black/10 dark:border-white/10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Card 01 */}
            <div className="relative overflow-hidden rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#111111] px-8 py-10 md:p-11 transition-colors duration-500 hover:border-black/25 dark:hover:border-white/25">
              <span className="absolute top-6 right-7 font-display font-semibold text-5xl leading-none text-black/[0.06] dark:text-white/10 select-none" aria-hidden="true">01</span>
              <div className="w-11 h-11 rounded-xl bg-[#D4E0DA] dark:bg-[#101F18] flex items-center justify-center mb-6">
                <Bot className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display font-semibold text-xl md:text-2xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight mb-2.5">
                Audit Readiness
              </h3>
              <p className="font-body text-[15px] text-black/55 dark:text-white/55 leading-snug mb-4">
                Collect evidence. Map controls. Close gaps.
              </p>
              <p className="font-body text-base text-black/70 dark:text-white/70 leading-relaxed">
                Nova prepares your organisation by automatically collecting evidence, mapping controls and tracking remediation activities across SOC&nbsp;2, HIPAA, GDPR, PCI&nbsp;DSS and ISO&nbsp;27001.
              </p>
            </div>

            {/* Card 02 */}
            <div className="relative overflow-hidden rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#111111] px-8 py-10 md:p-11 transition-colors duration-500 hover:border-black/25 dark:hover:border-white/25">
              <span className="absolute top-6 right-7 font-display font-semibold text-5xl leading-none text-black/[0.06] dark:text-white/10 select-none" aria-hidden="true">02</span>
              <div className="w-11 h-11 rounded-xl bg-[#EBD8C8] dark:bg-[#2E1B10] flex items-center justify-center mb-6">
                <ShieldCheck className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display font-semibold text-xl md:text-2xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight mb-2.5">
                Independent Audit Support
              </h3>
              <p className="font-body text-[15px] text-black/55 dark:text-white/55 leading-snug mb-4">
                Work with trusted independent auditors.
              </p>
              <p className="font-body text-base text-black/70 dark:text-white/70 leading-relaxed">
                Nova coordinates with trusted independent auditors who independently assess your controls and complete the audit process.
              </p>
            </div>
          </div>

          {/* Disclaimer banner */}
          <div className="mt-12 max-w-3xl mx-auto flex items-start gap-3 rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.025] dark:bg-white/[0.04] px-5 py-4">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-black/45 dark:text-white/50" strokeWidth={1.5} />
            <p className="font-body text-sm text-black/60 dark:text-white/60 leading-relaxed">
              Certification is issued by independent auditors. Nova coordinates the process and supports you throughout the engagement.
            </p>
          </div>
        </div>
      </section>

      {/* ===================== WHAT NOVA DOES ===================== */}
      <section className="px-6 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-center mb-24 leading-[1.1] tracking-tight">
            What Nova does for you
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-14">
            <div className="rounded-2xl bg-[#D4E0DA]/45 dark:bg-[#101F18]/45 px-8 py-10 md:p-12">
              <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-4">
                Pillar 01
              </p>
              <h3 className="font-display font-semibold text-xl md:text-2xl mb-8 leading-tight">
                Audit Readiness
              </h3>
              <ul className="space-y-4">
                {readinessBullets.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-1.5 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                    <span className="text-base md:text-lg font-body text-black/80 dark:text-white/85 leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-[#EBD8C8]/45 dark:bg-[#2E1B10]/45 px-8 py-10 md:p-12">
              <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-4">
                Pillar 02
              </p>
              <h3 className="font-display font-semibold text-xl md:text-2xl mb-8 leading-tight">
                Independent Auditor Coordination
              </h3>
              <ul className="space-y-4">
                {certificationBullets.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-1.5 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                    <span className="text-base md:text-lg font-body text-black/80 dark:text-white/85 leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-center mt-20 font-label text-xs uppercase tracking-[0.25em] text-black/55 dark:text-white/55">
            Real evidence. Real controls. Audit-ready compliance.
          </p>
        </div>
      </section>

      {/* ===================== PROCESS ===================== */}
      <section className="px-6 pb-24 md:pb-32">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight text-center mb-20">
            How it works
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
            {processSteps.map((step) => (
              <div
                key={step.num}
                data-testid={`process-step-${step.num}`}
                className="border-t border-black/10 dark:border-white/10 pt-6"
              >
                <span className="block font-display font-normal text-5xl md:text-6xl text-black/25 dark:text-white/35 leading-none mb-5 select-none">
                  {step.num}
                </span>
                <span className="block font-display font-semibold text-base md:text-lg text-[#0A0A0A] dark:text-[#F5F5F5] leading-snug">
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA (INVERTED) ===================== */}
      <section className="bg-[#0A0A0A] dark:bg-[#141414] py-24 md:py-32 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-[#FAFAFA] mb-10 leading-[1.1] tracking-tight">
            Start Your Compliance and Certification Journey
          </h2>
          <p className="font-body text-base md:text-lg text-white/70 mb-12 max-w-2xl mx-auto leading-relaxed">
            Begin with a free 60-minute assessment to understand your compliance gaps and prepare for independent certification.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="cta-start-now"
              className="font-label w-full sm:w-auto bg-[#0F62FE] dark:bg-[#2E79FF] text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] shadow-sm hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] hover:shadow-md transition-all duration-500"
            >
              Start Free Trial
            </button>
            <button
              type="button"
              onClick={() => navigate('/contact')}
              data-testid="cta-contact-sales"
              className="font-label w-full sm:w-auto border border-white/25 text-[#FAFAFA] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:bg-[#FAFAFA] dark:hover:bg-[#050505] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] transition-all duration-500"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
